import React, { useCallback, useEffect, useState } from 'react';
import { SimpleGrid, Text, useToast } from '@chakra-ui/react';
import { MdHistory, MdPeopleAlt, MdTune } from 'react-icons/md';
import { listStaff, getStaff } from '../../../repositories/staff.repository';
import { listActivityLogs } from '../../../repositories/activityLog.repository';
import { getSystemConfig } from '../../../repositories/systemConfig.repository';
import {
  getVillageSummary,
  getBuSiteSummary,
  getDashboardCounts,
} from '../../../repositories/dashboard.repository';
import { StaffFormModal } from '../staff/StaffFormModal';
import { StaffPasswordModal } from '../staff/StaffPasswordModal';
import {
  HubAmberButton,
  HubCard,
  HubError,
  HubFilterBar,
  HubGhostButton,
  HubLoading,
  HubPageHeader,
  HubPanel,
  HubRow,
  HubTable,
  hub,
} from './hubUi';

export function HubStaffHub({ push }) {
  const toast = useToast();
  const [data, setData] = useState(null);
  const [configCount, setConfigCount] = useState(null);
  const [activityTotal, setActivityTotal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const [counts, config, activities] = await Promise.all([
        getDashboardCounts(),
        getSystemConfig().catch(() => null),
        listActivityLogs({ page: 1, pageSize: 1 }).catch(() => null),
      ]);
      setData(counts);
      if (config && typeof config === 'object') {
        setConfigCount(Object.keys(config).length);
      } else if (Array.isArray(config)) {
        setConfigCount(config.length);
      } else {
        setConfigCount(null);
      }
      setActivityTotal(activities?.total != null ? Number(activities.total) : null);
    } catch (e) {
      setError(e.message || 'Failed to load staff counts');
      toast({ title: e.message || 'Failed to load staff counts', status: 'error' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  const staff = data?.staff || {};

  return (
    <>
      <HubPageHeader
        title="Staff"
        subtitle="Starting cards — directory, activities, and system config stay inside Hub."
      />
      <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
        <HubCard
          icon={MdPeopleAlt}
          label="Staff directory"
          value={String(staff.total ?? 0)}
          hint={`${staff.active ?? 0} active →`}
          onClick={() => push({ id: 'staffList', label: 'Directory' })}
        />
        <HubCard
          icon={MdHistory}
          label="Activities"
          value={activityTotal != null ? String(activityTotal) : '—'}
          hint="Who did what →"
          onClick={() => push({ id: 'activities', label: 'Activities' })}
        />
        <HubCard
          icon={MdTune}
          label="System config"
          value={configCount != null ? String(configCount) : '—'}
          hint="Live keys →"
          onClick={() => push({ id: 'systemConfig', label: 'System config' })}
        />
      </SimpleGrid>
    </>
  );
}

export function HubStaffList({ push }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [activeStaff, setActiveStaff] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listStaff({
        pageSize: 100,
        search: search.trim() || undefined,
      });
      setItems(res.items || []);
    } catch (e) {
      toast({ title: e.message || 'Failed to load staff', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, search]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <>
      <HubPageHeader
        title="Staff directory"
        subtitle="Live staff accounts. Add / edit / password use the same dialogs as Classic."
        actions={
          <>
            <HubAmberButton
              onClick={() => {
                setActiveStaff(null);
                setFormOpen(true);
              }}
            >
              Add staff
            </HubAmberButton>
            <HubGhostButton onClick={() => push({ id: 'activities', label: 'Activities' })}>
              Activities →
            </HubGhostButton>
          </>
        }
      />
      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search staff…"
        onClear={() => setSearch('')}
      />
      <HubTable
        loading={loading}
        rows={items}
        onRowClick={(r) =>
          push({
            id: 'staffDetail',
            label: r.fullName || r.name || r.email,
            itemId: r.id,
          })
        }
        columns={[
          {
            key: 'name',
            label: 'Name',
            render: (r) => r.fullName || r.name || '—',
          },
          {
            key: 'roles',
            label: 'Roles',
            render: (r) =>
              Array.isArray(r.roles)
                ? r.roles.map((x) => x.name || x).join(', ')
                : r.roleNames || '—',
          },
          { key: 'email', label: 'Email' },
          {
            key: 'lastLogin',
            label: 'Last login',
            render: (r) => r.lastLoginAt || r.lastLogin || '—',
          },
          {
            key: 'actions',
            label: '',
            render: (r) => (
              <HubGhostButton
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveStaff(r);
                  setFormOpen(true);
                }}
              >
                Edit
              </HubGhostButton>
            ),
          },
        ]}
      />

      <StaffFormModal
        isOpen={formOpen}
        onClose={() => {
          setFormOpen(false);
          setActiveStaff(null);
        }}
        staff={activeStaff}
        onSaved={load}
      />
    </>
  );
}

export function HubStaffDetail({ push, itemId }) {
  const [staff, setStaff] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const row = await getStaff(itemId);
      if (!row) throw new Error('Staff not found');
      setStaff(row);
    } catch (e) {
      setError(e.message || 'Failed to load staff');
    } finally {
      setLoading(false);
    }
  }, [itemId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;
  if (!staff) return <HubError message="Staff not found" />;

  const name = staff.fullName || staff.name || staff.email;

  return (
    <>
      <HubPageHeader
        title={name}
        subtitle={staff.email || 'Staff member'}
        actions={
          <>
            <HubAmberButton onClick={() => setFormOpen(true)}>Edit</HubAmberButton>
            <HubGhostButton onClick={() => setPasswordOpen(true)}>
              Update password
            </HubGhostButton>
            <HubGhostButton
              onClick={() =>
                push({
                  id: 'activities',
                  label: `${name} activity`,
                  staffId: staff.id,
                })
              }
            >
              Their activity →
            </HubGhostButton>
          </>
        }
      />
      <HubPanel>
        <HubRow label="Email">{staff.email}</HubRow>
        <HubRow label="Mobile">{staff.mobile || staff.phone}</HubRow>
        <HubRow label="Code">{staff.staffId || staff.code}</HubRow>
        <HubRow label="Active">{staff.isActive === false ? 'No' : 'Yes'}</HubRow>
        <HubRow label="Last login">{staff.lastLoginAt || staff.lastLogin}</HubRow>
        <HubRow label="Roles">
          {Array.isArray(staff.roles)
            ? staff.roles.map((x) => x.name || x).join(', ')
            : staff.roleNames || '—'}
        </HubRow>
      </HubPanel>

      <StaffFormModal
        isOpen={formOpen}
        onClose={() => setFormOpen(false)}
        staff={staff}
        onSaved={load}
      />
      <StaffPasswordModal
        isOpen={passwordOpen}
        onClose={() => setPasswordOpen(false)}
        staff={staff}
        onSaved={load}
      />
    </>
  );
}

export function HubActivities({ staffId }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await listActivityLogs({
        pageSize: 100,
        search: search.trim() || undefined,
        staffId: staffId || undefined,
      });
      setItems(res.items || []);
    } catch (e) {
      toast({ title: e.message || 'Failed to load activities', status: 'error' });
    } finally {
      setLoading(false);
    }
  }, [toast, search, staffId]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  return (
    <>
      <HubPageHeader
        title="Activities"
        subtitle="Live audit log from the API."
      />
      <HubFilterBar
        search={search}
        setSearch={setSearch}
        searchPlaceholder="Search module, action…"
        onClear={() => setSearch('')}
      />
      <HubTable
        loading={loading}
        rows={items.map((r, i) => ({ ...r, id: r.id ?? i }))}
        columns={[
          {
            key: 'when',
            label: 'When',
            render: (r) => r.createdAt || r.when || '—',
          },
          {
            key: 'staff',
            label: 'Staff',
            render: (r) => r.staffName || r.staff?.fullName || r.staffId || '—',
          },
          { key: 'module', label: 'Module', render: (r) => r.module || '—' },
          {
            key: 'action',
            label: 'Action',
            render: (r) => r.summary || r.action || '—',
          },
        ]}
      />
    </>
  );
}

export function HubSystemConfig() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getSystemConfig();
      const list = Array.isArray(data)
        ? data
        : Array.isArray(data?.items)
          ? data.items
          : Object.entries(data || {}).map(([key, value]) => ({
              key,
              value: typeof value === 'object' ? JSON.stringify(value) : String(value),
            }));
      setRows(
        list.map((r, i) => ({
          id: r.key || r.id || i,
          key: r.key || r.id,
          value: r.value ?? r.val ?? '',
        })),
      );
    } catch (e) {
      setError(e.message || 'Failed to load system config');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  if (loading) return <HubLoading />;
  if (error) return <HubError message={error} onRetry={load} />;

  return (
    <>
      <HubPageHeader
        title="System config"
        subtitle="Live configuration keys (read-only in Hub). Use Classic to edit if needed."
      />
      <HubPanel>
        {rows.length ? (
          rows.map((r) => (
            <HubRow key={r.id} label={r.key}>
              {String(r.value)}
            </HubRow>
          ))
        ) : (
          <Text fontSize="13px" color={hub.muted}>
            No config keys returned.
          </Text>
        )}
      </HubPanel>
    </>
  );
}

export function HubVillageReport({ push }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getVillageSummary();
        const list = Array.isArray(data) ? data : data?.items || data?.rows || [];
        setItems(
          list.map((r, i) => ({
            id: r.villageId || r.id || i,
            name: r.villageName || r.name || '—',
            members: r.memberCount ?? r.members ?? 0,
            equipment: r.equipmentCount ?? r.equipment ?? 0,
            deployed: r.deployedCount ?? r.deployed ?? 0,
          })),
        );
      } catch (e) {
        toast({ title: e.message || 'Failed to load village summary', status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  return (
    <>
      <HubPageHeader
        title="Village report"
        subtitle="Live village summary. Click a row to browse members."
      />
      <HubTable
        loading={loading}
        rows={items}
        onRowClick={(r) =>
          push({ id: 'memberList', label: `${r.name} members`, filter: 'all' })
        }
        columns={[
          { key: 'name', label: 'Village' },
          { key: 'members', label: 'Members' },
          { key: 'equipment', label: 'Equipment' },
          { key: 'deployed', label: 'Deployed' },
        ]}
      />
    </>
  );
}

export function HubBuReport({ push }) {
  const toast = useToast();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const data = await getBuSiteSummary({ includeEmpty: true });
        const list = Array.isArray(data) ? data : data?.items || data?.rows || [];
        setItems(
          list.map((r, i) => ({
            id: `${r.buId || 'bu'}-${r.siteId || i}`,
            name: r.siteName || '—',
            bu: r.buName || r.buCode || '—',
            indents: r.requestTotal ?? 0,
            equipment: r.equipmentAvailable ?? 0,
          })),
        );
      } catch (e) {
        toast({ title: e.message || 'Failed to load BU summary', status: 'error' });
      } finally {
        setLoading(false);
      }
    })();
  }, [toast]);

  return (
    <>
      <HubPageHeader
        title="BU / site utilization"
        subtitle="Live request and equipment stats per business unit / site."
        actions={
          push ? (
            <HubAmberButton
              onClick={() =>
                push({ id: 'masterBusinessUnits', label: 'Business units' })
              }
            >
              Edit BUs & sites
            </HubAmberButton>
          ) : null
        }
      />
      <HubTable
        loading={loading}
        rows={items}
        columns={[
          { key: 'bu', label: 'Business unit' },
          { key: 'name', label: 'Site' },
          { key: 'indents', label: 'Indents' },
          { key: 'equipment', label: 'Equipment' },
        ]}
      />
    </>
  );
}
